import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { GET, POST, PUT } from '../../../../configs/api';
import { api } from '../../../../utils/constants';

const formatCommentDateTime = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatCommentTime = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const isSameCalendarDay = (firstValue, secondValue) => {
  const first = new Date(firstValue);
  const second = new Date(secondValue);
  if (Number.isNaN(first.getTime()) || Number.isNaN(second.getTime())) return false;
  return (
    first.getFullYear() === second.getFullYear()
    && first.getMonth() === second.getMonth()
    && first.getDate() === second.getDate()
  );
};

const parseCommentsResponse = (data) => {
  if (Array.isArray(data)) {
    return { comments: data, mentionable_users: [] };
  }
  return {
    comments: Array.isArray(data?.comments) ? data.comments : [],
    mentionable_users: Array.isArray(data?.mentionable_users) ? data.mentionable_users : [],
  };
};

const getMentionQueryAtCaret = (text, caretIndex) => {
  const before = text.slice(0, caretIndex);
  const atIndex = before.lastIndexOf('@');
  if (atIndex === -1) return null;
  if (atIndex > 0 && !/\s/.test(before[atIndex - 1])) return null;
  const query = before.slice(atIndex + 1);
  if (/[\n\r\s]/.test(query)) return null;
  return { start: atIndex, query };
};

const filterMentionableUsers = (users, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  return users.filter((u) => {
    const label = (u.mention_label || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    return label.includes(q) || email.includes(q);
  });
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getMentionLabels = (users) => [
  ...new Set(
    (users || [])
      .flatMap((u) => [u.mention_label, u.email])
      .filter(Boolean)
      .map((label) => label.trim()),
  ),
].sort((a, b) => b.length - a.length);

const MENTION_ALL_LABEL = 'all';
const MENTION_ALL_OPTION_ID = '__mention_all__';

const splitCommentTextWithMentions = (text, mentionLabelStrings) => {
  if (!text) return [{ type: 'text', value: '' }];

  const labels = [...new Set((mentionLabelStrings || []).filter(Boolean).map((l) => l.trim()))]
    .filter((l) => l.toLowerCase() !== MENTION_ALL_LABEL)
    .sort((a, b) => b.length - a.length);

  const labelPattern = labels.length > 0
    ? `@(${labels.map(escapeRegExp).join('|')})`
    : null;
  const pattern = new RegExp(
    labelPattern ? `@${MENTION_ALL_LABEL}\\b|${labelPattern}` : `@${MENTION_ALL_LABEL}\\b`,
    'gi',
  );

  const parts = [];
  let lastIndex = 0;
  let match = pattern.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    const mentionValue = match[0].toLowerCase() === `@${MENTION_ALL_LABEL}`
      ? MENTION_ALL_LABEL
      : match[1];
    parts.push({ type: 'mention', value: mentionValue });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', value: text }];
};

const shouldShowMentionAllOption = (query) => {
  const q = query.trim().toLowerCase();
  return !q || MENTION_ALL_LABEL.startsWith(q) || q.startsWith('a');
};

const isCommentEdited = (comment) => {
  if (!comment?.updated_at || !comment?.created_at) return false;
  return new Date(comment.updated_at).getTime() > new Date(comment.created_at).getTime();
};

const extractMentionedUserIdsFromText = (text, users) => {
  if (!text || !users?.length) return [];
  const ids = [];
  users.forEach((mentionUser) => {
    const label = mentionUser.mention_label || mentionUser.email;
    if (!label) return;
    if (new RegExp(`\\B@${escapeRegExp(label)}\\b`, 'i').test(text)) {
      ids.push(mentionUser.id);
    }
  });
  return ids;
};

const buildMentionMenuOptions = (users, query) => {
  const filteredUsers = filterMentionableUsers(users, query);
  const mentionAllOption = {
    id: MENTION_ALL_OPTION_ID,
    mention_label: MENTION_ALL_LABEL,
    email: 'Notify everyone with access to this job',
    isMentionAll: true,
  };
  return shouldShowMentionAllOption(query)
    ? [mentionAllOption, ...filteredUsers]
    : filteredUsers;
};

const getMentionChipSx = (isDarkMode, isOwn) => {
  if (isOwn) {
    return isDarkMode
      ? {
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: '#f0f9ff',
          border: '1px solid rgba(255, 255, 255, 0.28)',
        }
      : {
          backgroundColor: 'rgba(29, 78, 216, 0.18)',
          color: '#1e3a8a',
          border: '1px solid rgba(29, 78, 216, 0.22)',
        };
  }
  return isDarkMode
    ? {
        backgroundColor: 'rgba(96, 165, 250, 0.22)',
        color: '#bfdbfe',
        border: '1px solid rgba(96, 165, 250, 0.35)',
      }
    : {
        backgroundColor: 'rgba(59, 130, 246, 0.14)',
        color: '#1d4ed8',
        border: '1px solid rgba(59, 130, 246, 0.25)',
      };
};

const CommentTextWithMentions = ({ text, mentionLabels, isDarkMode, isOwn }) => {
  const parts = useMemo(
    () => splitCommentTextWithMentions(text, mentionLabels),
    [text, mentionLabels],
  );
  const mentionSx = getMentionChipSx(isDarkMode, isOwn);

  return (
    <Typography
      component="span"
      variant="body2"
      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
    >
      {parts.map((part, index) => {
        if (part.type === 'mention') {
          return (
            <Box
              key={`mention-${index}-${part.value}`}
              component="span"
              sx={{
                ...mentionSx,
                display: 'inline',
                borderRadius: '4px',
                px: 0.5,
                py: 0.125,
                mx: 0.125,
                fontWeight: 600,
                lineHeight: 1.45,
                boxDecorationBreak: 'clone',
                WebkitBoxDecorationBreak: 'clone',
              }}
            >
              @{part.value}
            </Box>
          );
        }
        return (
          <Box key={`text-${index}`} component="span">
            {part.value}
          </Box>
        );
      })}
    </Typography>
  );
};

const CommentsTab = ({ jobId, user, loadingAdditionalData, onCommentsCountChange, initialComments }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [comments, setComments] = useState([]);
  const [mentionableUsers, setMentionableUsers] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionAll, setMentionAll] = useState(false);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editMentionedUserIds, setEditMentionedUserIds] = useState([]);
  const [editMentionAll, setEditMentionAll] = useState(false);
  const [editMentionMenuOpen, setEditMentionMenuOpen] = useState(false);
  const [editMentionQuery, setEditMentionQuery] = useState('');
  const [editMentionHighlightIndex, setEditMentionHighlightIndex] = useState(0);
  const [editingSubmitting, setEditingSubmitting] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const composerRef = useRef(null);
  const editInputRef = useRef(null);
  const editComposerRef = useRef(null);

  useEffect(() => {
    const parsed = parseCommentsResponse({ comments: initialComments });
    setComments(parsed.comments);
  }, [initialComments]);

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      const first = new Date(a.created_at).getTime();
      const second = new Date(b.created_at).getTime();
      return first - second;
    });
  }, [comments]);

  const mentionMenuOptions = useMemo(
    () => buildMentionMenuOptions(mentionableUsers, mentionQuery),
    [mentionableUsers, mentionQuery],
  );

  const editMentionMenuOptions = useMemo(
    () => buildMentionMenuOptions(mentionableUsers, editMentionQuery),
    [mentionableUsers, editMentionQuery],
  );

  const mentionLabelsForDisplay = useMemo(
    () => [MENTION_ALL_LABEL, ...getMentionLabels(mentionableUsers)],
    [mentionableUsers],
  );

  useEffect(() => {
    if (mentionHighlightIndex >= mentionMenuOptions.length) {
      setMentionHighlightIndex(0);
    }
  }, [mentionMenuOptions.length, mentionHighlightIndex]);

  useEffect(() => {
    if (editMentionHighlightIndex >= editMentionMenuOptions.length) {
      setEditMentionHighlightIndex(0);
    }
  }, [editMentionMenuOptions.length, editMentionHighlightIndex]);

  const fetchComments = async () => {
    if (!jobId) {
      setComments([]);
      setMentionableUsers([]);
      if (onCommentsCountChange) onCommentsCountChange(0);
      return;
    }

    setLoadingComments(true);
    setError('');
    const { data, status, message } = await GET(`${api.getJobComments}/${jobId}/comments`);
    if (status === 200) {
      const parsed = parseCommentsResponse(data);
      setComments(parsed.comments);
      setMentionableUsers(parsed.mentionable_users);
      if (onCommentsCountChange) onCommentsCountChange(parsed.comments.length);
    } else {
      setError(message || 'Failed to load comments.');
    }
    setLoadingComments(false);
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    if (!bottomRef.current) return;
    bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [sortedComments.length]);

  const updateMentionMenuFromInput = (text, caretIndex) => {
    const mentionState = getMentionQueryAtCaret(text, caretIndex);
    if (!mentionState) {
      setMentionMenuOpen(false);
      setMentionQuery('');
      return;
    }
    setMentionMenuOpen(true);
    setMentionQuery(mentionState.query);
    setMentionHighlightIndex(0);
  };

  const insertMentionText = (label) => {
    const input = inputRef.current;
    const caret = input?.selectionStart ?? commentText.length;
    const mentionState = getMentionQueryAtCaret(commentText, caret);
    if (!mentionState) return null;

    const before = commentText.slice(0, mentionState.start);
    const after = commentText.slice(caret);
    const insertion = `@${label} `;
    const nextText = `${before}${insertion}${after}`;
    const nextCaret = before.length + insertion.length;

    setCommentText(nextText);
    setMentionMenuOpen(false);
    setMentionQuery('');

    setTimeout(() => {
      if (input) {
        input.focus();
        input.setSelectionRange(nextCaret, nextCaret);
      }
    }, 0);

    return nextText;
  };

  const handleCommentChange = (event) => {
    const next = event.target.value;
    setCommentText(next);
    setMentionAll(/\B@all\b/i.test(next));
    updateMentionMenuFromInput(next, event.target.selectionStart ?? next.length);
  };

  const insertMention = (mentionOption) => {
    if (mentionOption?.isMentionAll) {
      insertMentionText(MENTION_ALL_LABEL);
      setMentionAll(true);
      setMentionedUserIds([]);
      return;
    }

    const label = mentionOption.mention_label || mentionOption.email || 'user';
    insertMentionText(label);
    setMentionAll(false);
    setMentionedUserIds((prev) => (
      prev.includes(mentionOption.id) ? prev : [...prev, mentionOption.id]
    ));
  };

  const resetEditState = () => {
    setEditingCommentId(null);
    setEditText('');
    setEditMentionedUserIds([]);
    setEditMentionAll(false);
    setEditMentionMenuOpen(false);
    setEditMentionQuery('');
    setEditMentionHighlightIndex(0);
  };

  const startEdit = (comment) => {
    const text = comment.comment_text || '';
    setEditingCommentId(comment.id);
    setEditText(text);
    setEditMentionAll(/\B@all\b/i.test(text));
    setEditMentionedUserIds(extractMentionedUserIdsFromText(text, mentionableUsers));
    setEditMentionMenuOpen(false);
    setEditMentionQuery('');
    setEditMentionHighlightIndex(0);
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
      }
    }, 0);
  };

  const cancelEdit = () => {
    resetEditState();
  };

  const updateEditMentionMenuFromInput = (text, caretIndex) => {
    const mentionState = getMentionQueryAtCaret(text, caretIndex);
    if (!mentionState) {
      setEditMentionMenuOpen(false);
      setEditMentionQuery('');
      return;
    }
    setEditMentionMenuOpen(true);
    setEditMentionQuery(mentionState.query);
    setEditMentionHighlightIndex(0);
  };

  const insertEditMentionText = (label) => {
    const input = editInputRef.current;
    const caret = input?.selectionStart ?? editText.length;
    const mentionState = getMentionQueryAtCaret(editText, caret);
    if (!mentionState) return null;

    const before = editText.slice(0, mentionState.start);
    const after = editText.slice(caret);
    const insertion = `@${label} `;
    const nextText = `${before}${insertion}${after}`;
    const nextCaret = before.length + insertion.length;

    setEditText(nextText);
    setEditMentionMenuOpen(false);
    setEditMentionQuery('');

    setTimeout(() => {
      if (input) {
        input.focus();
        input.setSelectionRange(nextCaret, nextCaret);
      }
    }, 0);

    return nextText;
  };

  const handleEditChange = (event) => {
    const next = event.target.value;
    const hasMentionAll = /\B@all\b/i.test(next);
    setEditText(next);
    setEditMentionAll(hasMentionAll);
    if (!hasMentionAll) {
      setEditMentionedUserIds(extractMentionedUserIdsFromText(next, mentionableUsers));
    }
    updateEditMentionMenuFromInput(next, event.target.selectionStart ?? next.length);
  };

  const insertEditMention = (mentionOption) => {
    if (mentionOption?.isMentionAll) {
      insertEditMentionText(MENTION_ALL_LABEL);
      setEditMentionAll(true);
      setEditMentionedUserIds([]);
      return;
    }

    const label = mentionOption.mention_label || mentionOption.email || 'user';
    insertEditMentionText(label);
    setEditMentionAll(false);
    setEditMentionedUserIds((prev) => (
      prev.includes(mentionOption.id) ? prev : [...prev, mentionOption.id]
    ));
  };

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed) {
      setError('Comment cannot be empty.');
      return;
    }
    if (!jobId || !editingCommentId) {
      setError('Comment is not available.');
      return;
    }

    setEditingSubmitting(true);
    setError('');
    const { data, status, message } = await PUT(
      `${api.updateJobComment}/${jobId}/comments/${editingCommentId}`,
      {},
      {
        comment_text: trimmed,
        mentioned_user_ids: editMentionAll ? [] : editMentionedUserIds,
        mention_all: editMentionAll,
      },
    );

    if (status === 200) {
      setComments((prev) => prev.map((comment) => (
        comment.id === editingCommentId ? { ...comment, ...data } : comment
      )));
      resetEditState();
    } else {
      setError(message || 'Failed to update comment.');
    }
    setEditingSubmitting(false);
  };

  const onEditInputKeyDown = (event) => {
    if (editMentionMenuOpen && editMentionMenuOptions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setEditMentionHighlightIndex((i) => (i + 1) % editMentionMenuOptions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setEditMentionHighlightIndex((i) => (
          i === 0 ? editMentionMenuOptions.length - 1 : i - 1
        ));
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertEditMention(editMentionMenuOptions[editMentionHighlightIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setEditMentionMenuOpen(false);
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!editingSubmitting) {
        handleSaveEdit();
      }
    }
  };

  const handleSendComment = async () => {
    const trimmed = commentText.trim();
    if (!trimmed) {
      setError('Comment cannot be empty.');
      return;
    }
    if (!jobId) {
      setError('Job is not available.');
      return;
    }

    setSubmitting(true);
    setError('');
    const { data, status, message } = await POST(
      `${api.createJobComment}/${jobId}/comments`,
      {},
      {
        comment_text: trimmed,
        mentioned_user_ids: mentionAll ? [] : mentionedUserIds,
        mention_all: mentionAll,
      },
    );

    if (status === 201) {
      setComments((prev) => {
        const next = [...prev, data];
        if (onCommentsCountChange) onCommentsCountChange(next.length);
        return next;
      });
      setCommentText('');
      setMentionedUserIds([]);
      setMentionAll(false);
      setMentionMenuOpen(false);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } else {
      setError(message || 'Failed to post comment.');
    }
    setSubmitting(false);
  };

  const onInputKeyDown = (event) => {
    if (mentionMenuOpen && mentionMenuOptions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionHighlightIndex((i) => (i + 1) % mentionMenuOptions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionHighlightIndex((i) => (
          i === 0 ? mentionMenuOptions.length - 1 : i - 1
        ));
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        insertMention(mentionMenuOptions[mentionHighlightIndex]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMentionMenuOpen(false);
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!submitting) {
        handleSendComment();
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Comments
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {loadingAdditionalData || loadingComments ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb',
            backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc',
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minHeight: 0,
          }}
        >
          {sortedComments.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              No comments yet. Start the conversation.
            </Typography>
          ) : (
            sortedComments.map((comment) => {
              const isOwn = comment.created_by === user?.id;
              const isEditing = editingCommentId === comment.id;
              const author =
                comment.created_by_name ||
                comment.created_by_email ||
                'Unknown user';
              return (
                <Box
                  key={comment.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isOwn ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '75%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      borderTopRightRadius: isOwn ? 0.5 : 2,
                      borderTopLeftRadius: isOwn ? 2 : 0.5,
                      backgroundColor: isOwn
                        ? (isDarkMode ? '#2563eb' : '#dbeafe')
                        : (isDarkMode ? '#1f2937' : '#ffffff'),
                      border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontWeight: 600 }}
                      >
                        {author}
                      </Typography>
                      {isOwn && !isEditing && (
                        <IconButton
                          size="small"
                          aria-label="Edit comment"
                          onClick={() => startEdit(comment)}
                          disabled={editingSubmitting || submitting}
                          sx={{ p: 0.25, color: 'text.secondary' }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                    {isEditing ? (
                      <Box ref={editComposerRef} sx={{ position: 'relative' }}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={5}
                          value={editText}
                          onChange={handleEditChange}
                          onKeyDown={onEditInputKeyDown}
                          onClick={() => {
                            const input = editInputRef.current;
                            const text = input?.value ?? editText;
                            const caret = input?.selectionStart ?? text.length;
                            updateEditMentionMenuFromInput(text, caret);
                          }}
                          disabled={editingSubmitting}
                          inputRef={editInputRef}
                          size="small"
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.75 }}>
                          <IconButton
                            size="small"
                            aria-label="Cancel edit"
                            onClick={cancelEdit}
                            disabled={editingSubmitting}
                            sx={{
                              color: isDarkMode ? '#f87171' : '#dc2626',
                              '&:hover': {
                                backgroundColor: isDarkMode
                                  ? 'rgba(248, 113, 113, 0.12)'
                                  : 'rgba(220, 38, 38, 0.1)',
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="Save edit"
                            onClick={handleSaveEdit}
                            disabled={editingSubmitting || !editText.trim()}
                            sx={{
                              color: isDarkMode ? '#4ade80' : '#16a34a',
                              '&:hover': {
                                backgroundColor: isDarkMode
                                  ? 'rgba(74, 222, 128, 0.12)'
                                  : 'rgba(22, 163, 74, 0.1)',
                              },
                            }}
                          >
                            {editingSubmitting ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                        <Popper
                          open={editMentionMenuOpen && editMentionMenuOptions.length > 0}
                          anchorEl={editComposerRef.current}
                          placement="top-start"
                          sx={{ zIndex: (t) => t.zIndex.modal + 1, width: editComposerRef.current?.clientWidth }}
                        >
                          <Paper
                            elevation={4}
                            sx={{
                              maxHeight: 220,
                              overflow: 'auto',
                              border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb',
                            }}
                          >
                            <List dense disablePadding>
                              {editMentionMenuOptions.map((mentionOption, index) => (
                                <ListItemButton
                                  key={mentionOption.id}
                                  selected={index === editMentionHighlightIndex}
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => insertEditMention(mentionOption)}
                                >
                                  <ListItemText
                                    primary={mentionOption.isMentionAll
                                      ? `@${MENTION_ALL_LABEL}`
                                      : (mentionOption.mention_label || mentionOption.email)}
                                    secondary={mentionOption.isMentionAll
                                      ? mentionOption.email
                                      : mentionOption.email}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      sx: mentionOption.isMentionAll ? { fontWeight: 600 } : undefined,
                                    }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </Paper>
                        </Popper>
                      </Box>
                    ) : (
                      <CommentTextWithMentions
                        text={comment.comment_text}
                        mentionLabels={mentionLabelsForDisplay}
                        isDarkMode={isDarkMode}
                        isOwn={isOwn}
                      />
                    )}
                    {!isEditing && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
                        {formatCommentDateTime(comment.created_at)}
                        {isCommentEdited(comment) && (
                          <Box component="span" sx={{ ml: 0.5, fontStyle: 'italic' }}>
                            · edited{' '}
                            {isSameCalendarDay(comment.created_at, comment.updated_at)
                              ? formatCommentTime(comment.updated_at)
                              : formatCommentDateTime(comment.updated_at)}
                          </Box>
                        )}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
          <Box ref={bottomRef} />
        </Box>
      )}

      <Box ref={composerRef} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, position: 'relative' }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          placeholder="Write a comment... Use @ to mention someone or @all"
          value={commentText}
          onChange={handleCommentChange}
          onKeyDown={onInputKeyDown}
          onClick={() => {
            const input = inputRef.current;
            const text = input?.value ?? commentText;
            const caret = input?.selectionStart ?? text.length;
            updateMentionMenuFromInput(text, caret);
          }}
          disabled={submitting || !jobId || Boolean(editingCommentId)}
          inputRef={inputRef}
        />
        <IconButton
          color="primary"
          onClick={handleSendComment}
          disabled={submitting || !commentText.trim() || !jobId}
          sx={{ mb: 0.5 }}
        >
          {submitting ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>

        <Popper
          open={mentionMenuOpen && mentionMenuOptions.length > 0}
          anchorEl={composerRef.current}
          placement="top-start"
          sx={{ zIndex: (t) => t.zIndex.modal + 1, width: composerRef.current?.clientWidth }}
        >
          <Paper
            elevation={4}
            sx={{
              maxHeight: 220,
              overflow: 'auto',
              border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb',
            }}
          >
            <List dense disablePadding>
              {mentionMenuOptions.map((mentionOption, index) => (
                <ListItemButton
                  key={mentionOption.id}
                  selected={index === mentionHighlightIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertMention(mentionOption)}
                >
                  <ListItemText
                    primary={mentionOption.isMentionAll
                      ? `@${MENTION_ALL_LABEL}`
                      : (mentionOption.mention_label || mentionOption.email)}
                    secondary={mentionOption.isMentionAll
                      ? mentionOption.email
                      : mentionOption.email}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: mentionOption.isMentionAll ? { fontWeight: 600 } : undefined,
                    }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </Box>
  );
};

export default CommentsTab;
